'use client';

import {
  Button,
  Modal,
  ModalDialog,
  ModalOverlay,
  PinInput
} from '@goodneighbor/ui';
import { MfaLoginState } from '@/hooks/useAuthHooks';
import { UseMutationResult } from '@tanstack/react-query';
import useMfaLoginModal from './hooks/useMfaLoginModal';
import styles from './MfaLoginModal.module.scss';

type MfaLoginModalProps = {
  mfaLoginState: MfaLoginState;
  verifyMfaLoginMutation: UseMutationResult<
    unknown,
    Error,
    { otp: string; mfaToken: string }
  >;
  resendMfaLoginMutation: UseMutationResult<unknown, Error, string>;
  onCancel: () => void;
};

export default function MfaLoginModal({
  mfaLoginState,
  verifyMfaLoginMutation,
  resendMfaLoginMutation,
  onCancel
}: MfaLoginModalProps) {
  const {
    otp,
    setOtp,
    error,
    resendCooldown,
    handleVerify,
    handleResend,
    isVerifying,
    isResending
  } = useMfaLoginModal({
    mfaLoginState,
    verifyMfaLoginMutation,
    resendMfaLoginMutation
  });

  const isBusy = isVerifying || isResending;

  return (
    <ModalOverlay
      className={styles.overlay}
      isDismissable={false}
      isOpen={true}
      onOpenChange={() => {}}
    >
      <Modal>
        <ModalDialog aria-labelledby="Verify your identity">
          <div className={styles.modal}>
            <div className={styles.header}>
              <h2 className={styles.title}>Verify Your Identity</h2>
            </div>
            <div className={styles.content}>
              <p className={styles.subtitle}>
                Enter the 6-digit code sent to{' '}
                <strong>{mfaLoginState.maskedContact}</strong>.
              </p>
              <PinInput size="md">
                <PinInput.Group
                  maxLength={6}
                  value={otp}
                  onChange={setOtp}
                  onComplete={handleVerify}
                >
                  <PinInput.Slot index={0} className={styles.pinSlot} />
                  <PinInput.Slot index={1} className={styles.pinSlot} />
                  <PinInput.Slot index={2} className={styles.pinSlot} />
                  <PinInput.Separator />
                  <PinInput.Slot index={3} className={styles.pinSlot} />
                  <PinInput.Slot index={4} className={styles.pinSlot} />
                  <PinInput.Slot index={5} className={styles.pinSlot} />
                </PinInput.Group>
              </PinInput>
              {error && <p className={styles.error}>{error}</p>}
              <button
                className={styles.resendLink}
                onClick={handleResend}
                disabled={isBusy || resendCooldown > 0}
              >
                {isResending
                  ? 'Sending...'
                  : resendCooldown > 0
                    ? resendCooldown > 60
                      ? `Resend code in ${Math.floor(resendCooldown / 60)}m ${resendCooldown % 60}s`
                      : `Resend code in ${resendCooldown}s`
                    : "Didn't receive a code? Resend"}
              </button>
              <div className={styles.footer}>
                <Button
                  color="secondary"
                  onClick={onCancel}
                  isDisabled={isBusy}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onClick={handleVerify}
                  isDisabled={otp.length < 6 || isBusy}
                >
                  {isVerifying ? 'Verifying...' : 'Verify'}
                </Button>
              </div>
            </div>
          </div>
        </ModalDialog>
      </Modal>
    </ModalOverlay>
  );
}
